/**
 * Tagada Rx — brand statement (affiliate mode, Tagada is merchant of record).
 *
 * One screen whatever the acquirer or settlement method: what was sold, every
 * deduction with its source, what was paid out and what is owed now.
 * docs/RX_PAYMENTS_DESIGN.md §3.7.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAccountManager } from '@/hooks/use-account-manager';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TableCard } from '@/components/ui/table-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { RxPageShell } from '@/components/rx';
import { formatCents, useRxPayoutAction, useRxStatement } from '@/lib/api/rx';

type StatementCharge = {
  id: string;
  kind: string;
  amountMinor: number;
  currency: string;
  basis: Record<string, unknown>;
  status: string;
  method: string | null;
  reference: string | null;
  collectedAt: string | null;
};

type StatementOrder = {
  orderId: string | null;
  rxCaseId: string | null;
  date: string;
  currency: string;
  saleMinor: number;
  declined: boolean;
  charges: StatementCharge[];
  totalChargesMinor: number;
  netMinor: number;
};

const pct = (bps: unknown) => `${(Number(bps ?? 0) / 100).toFixed(2).replace(/\.00$/, '')}%`;
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

const CHARGE_STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  collected: 'default',
  due: 'outline',
  invoiced: 'secondary',
  waived: 'outline',
};

function Kpi({ label, value, hint }: { label: string; value: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      {hint ? <CardContent className="text-muted-foreground pt-0 text-xs">{hint}</CardContent> : null}
    </Card>
  );
}

function ChargeRows({ order }: { order: StatementOrder }) {
  const { t } = useTranslation('rx');
  const m = (v: unknown, c = order.currency) => formatCents(Number(v ?? 0), c);
  const how = (c: StatementCharge) => {
    const b = c.basis;
    if (c.kind === 'crm_fee') return t('statement.how.rateOf', { rate: pct(b.rateBps), base: m(b.baseMinor) });
    if (c.kind === 'processing')
      return t('statement.how.rateOfPlusFixed', { rate: pct(b.rateBps), base: m(b.baseMinor), fixed: m(b.fixedMinor) });
    if (c.kind === 'medication') return t('statement.how.pharmacy');
    return t('statement.how.network');
  };
  return (
    <div className="space-y-1 py-2">
      {order.charges.map((c) => (
        <div key={c.id} className="grid grid-cols-12 items-center gap-2 text-sm">
          <div className="col-span-3 font-medium">{String(t(`statement.kind.${c.kind}`, { defaultValue: c.kind }))}</div>
          <div className="text-muted-foreground col-span-3 text-xs">{how(c)}</div>
          <div className="col-span-2 text-right tabular-nums">−{m(c.amountMinor, c.currency)}</div>
          <div className="col-span-2 text-xs">
            {c.method
              ? String(t(`statement.method.${c.method}`, { defaultValue: c.method }))
              : t('statement.method.pending')}
          </div>
          <div className="col-span-2 flex flex-col items-end gap-0.5 text-right">
            <Badge variant={CHARGE_STATUS_VARIANT[c.status] ?? 'outline'} className="text-xs">
              {String(t(`statement.status.${c.status}`, { defaultValue: c.status }))}
            </Badge>
            <span className="text-muted-foreground text-[11px]">
              {[day(c.collectedAt), c.reference].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      ))}
      {order.charges.some((c) => c.basis.configured === false) ? (
        <p className="text-muted-foreground text-xs">{t('statement.costsIncomplete')}</p>
      ) : null}
    </div>
  );
}

type PayoutRow = {
  id: string;
  date: string;
  currency: string;
  amountMinor: number;
  rail: string;
  status: string;
  reference: string | null;
  failureReason: string | null;
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  paid: 'default',
  sent: 'secondary',
  pending: 'secondary',
  awaiting_ops: 'outline',
  failed: 'destructive',
  cancelled: 'outline',
};

function PayoutsCard({ payouts, onChanged }: { payouts: PayoutRow[]; onChanged: () => void }) {
  const { t } = useTranslation('rx');
  const { isAccountManager } = useAccountManager();
  const { toast } = useToast();
  const action = useRxPayoutAction();
  const [notes, setNotes] = React.useState<Record<string, string>>({});

  const run = async (payoutId: string, kind: 'approve' | 'mark_paid' | 'cancel') => {
    try {
      await action.mutateAsync({ payoutId, action: kind, note: notes[payoutId] });
      toast({ title: t('statement.payouts.toast.done') });
      onChanged();
    } catch (err) {
      toast({
        title: t('statement.payouts.toast.failed'),
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    }
  };

  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('statement.payouts.date')}</TableHead>
            <TableHead className="text-right">{t('statement.payouts.amount')}</TableHead>
            <TableHead>{t('statement.payouts.rail')}</TableHead>
            <TableHead>{t('statement.payouts.status')}</TableHead>
            <TableHead>{t('statement.payouts.reference')}</TableHead>
            {isAccountManager ? <TableHead className="text-right">{t('statement.payouts.actions')}</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {payouts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isAccountManager ? 6 : 5} className="text-muted-foreground py-6 text-center">
                {t('statement.payouts.empty')}
              </TableCell>
            </TableRow>
          ) : (
            payouts.map((p) => {
              const open = p.status === 'awaiting_ops' || p.status === 'pending';
              return (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.date}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCents(p.amountMinor, p.currency)}</TableCell>
                  <TableCell>{String(t(`statement.payouts.rails.${p.rail}`, { defaultValue: p.rail }))}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[p.status] ?? 'outline'}>
                      {String(t(`statement.payouts.statuses.${p.status}`, { defaultValue: p.status }))}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">{p.reference ?? p.failureReason ?? ''}</TableCell>
                  {isAccountManager ? (
                    <TableCell className="text-right">
                      {open ? (
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            className="h-8 w-40"
                            placeholder={t('statement.payouts.notePlaceholder')}
                            value={notes[p.id] ?? ''}
                            onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                          />
                          {p.rail !== 'mercury' && p.status === 'awaiting_ops' ? (
                            <Button size="sm" disabled={action.isPending} onClick={() => run(p.id, 'approve')}>
                              {t('statement.payouts.approve')}
                            </Button>
                          ) : null}
                          <Button size="sm" variant="secondary" disabled={action.isPending} onClick={() => run(p.id, 'mark_paid')}>
                            {t('statement.payouts.markPaid')}
                          </Button>
                          <Button size="sm" variant="ghost" disabled={action.isPending} onClick={() => run(p.id, 'cancel')}>
                            {t('statement.payouts.cancel')}
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableCard>
  );
}

export default function RxStatementPage() {
  const { t } = useTranslation('rx');
  const { id: storeId } = useStoreNavigate();
  const query = useRxStatement(storeId);
  const [open, setOpen] = React.useState<string | null>(null);

  const data = query.data;

  return (
    <RxPageShell title={t('statement.title')} description={t('statement.description')}>
      {query.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('statement.loading')}
        </div>
      ) : !data || !data.available ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('statement.unavailable.title')}</CardTitle>
            <CardDescription>
              {data && !data.available && data.reason === 'rx_payments_setting_up'
                ? t('statement.unavailable.settingUp')
                : t('statement.unavailable.notEnabled')}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Kpi
              label={t('statement.kpi.collected')}
              value={formatCents(data.summary.collectedMinor, data.currency)}
              hint={t('statement.kpi.collectedHint')}
            />
            <Kpi
              label={t('statement.kpi.due')}
              value={formatCents(data.summary.dueMinor, data.currency)}
              hint={t('statement.kpi.dueHint', { days: data.summary.invoiceAfterDays })}
            />
            <Kpi
              label={t('statement.kpi.invoiced')}
              value={formatCents(data.summary.invoicedMinor, data.currency)}
              hint={t('statement.kpi.invoicedHint')}
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('statement.table.title')}</h3>
            <TableCard>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>{t('statement.table.date')}</TableHead>
                    <TableHead>{t('statement.table.order')}</TableHead>
                    <TableHead className="text-right">{t('statement.table.sale')}</TableHead>
                    <TableHead className="text-right">{t('statement.table.charges')}</TableHead>
                    <TableHead className="text-right">{t('statement.table.net')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                        {t('statement.table.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data.orders as StatementOrder[]).map((order) => {
                      const key = `${order.orderId}|${order.rxCaseId}`;
                      const isOpen = open === key;
                      return (
                        <React.Fragment key={key}>
                          <TableRow className="cursor-pointer" onClick={() => setOpen(isOpen ? null : key)}>
                            <TableCell>{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                            <TableCell className="font-medium">{day(order.date)}</TableCell>
                            <TableCell className="text-xs">
                              {order.orderId}
                              {order.declined ? (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {t('statement.table.declined')}
                                </Badge>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{formatCents(order.saleMinor, order.currency)}</TableCell>
                            <TableCell className="text-right tabular-nums">−{formatCents(order.totalChargesMinor, order.currency)}</TableCell>
                            <TableCell className="text-right font-medium tabular-nums">{formatCents(order.netMinor, order.currency)}</TableCell>
                          </TableRow>
                          {isOpen ? (
                            <TableRow>
                              <TableCell />
                              <TableCell colSpan={5}>
                                <ChargeRows order={order} />
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableCard>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('statement.payouts.title')}</h3>
            {data.payoutSystem === 'rx_payouts' ? (
              <PayoutsCard payouts={data.payouts as PayoutRow[]} onChanged={() => void query.refetch()} />
            ) : (
              <p className="text-muted-foreground text-sm">
                {data.payoutsReady ? t('statement.payouts.tagadapay') : t('statement.payouts.heldUntilVerified')}
              </p>
            )}
          </div>
        </div>
      )}
    </RxPageShell>
  );
}
